// Ref -> commit resolution (one API call) and raw file URLs at a pinned commit.
import type { Endpoints } from './config.ts';
import { RtwError } from './errors.ts';

export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

export async function resolveCommit(ep: Endpoints, owner: string, repo: string, ref: string | undefined, fetchFn: FetchFn = fetch): Promise<string> {
  const r = ref ?? 'HEAD';
  if (/^[0-9a-f]{40}$/.test(r)) return r; // already a full commit: no API call
  const url = `${ep.api}/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/commits/${encodeURIComponent(r)}`;
  let res: Response;
  try {
    res = await fetchFn(url, { headers: { Accept: 'application/vnd.github+json' }, credentials: 'omit', referrerPolicy: 'no-referrer' });
  } catch (e) {
    throw new RtwError('network', 'Could not reach GitHub. Check your connection.', `${url}: ${String(e)}`);
  }
  if (res.status === 404 || res.status === 422) {
    throw new RtwError('not-found', `GitHub has no public repository ${owner}/${repo} with "${r}".`, `${url}: HTTP ${res.status}`);
  }
  if (res.status === 403 || res.status === 429) {
    const reset = res.headers.get('x-ratelimit-reset');
    const when = reset ? new Date(Number(reset) * 1000).toLocaleTimeString() : 'later';
    throw new RtwError('rate-limit', `GitHub's hourly limit for this network is used up. Try again at ${when}.`, `${url}: HTTP ${res.status}`);
  }
  if (!res.ok) throw new RtwError('network', 'GitHub returned an error.', `${url}: HTTP ${res.status}`);
  const body = (await res.json().catch(() => null)) as { sha?: unknown } | null;
  const sha = body?.sha;
  if (typeof sha !== 'string' || !/^[0-9a-f]{40}$/.test(sha)) throw new RtwError('network', 'GitHub sent an unexpected reply.', `${url}: no commit sha`);
  return sha;
}

export function rawUrl(ep: Endpoints, owner: string, repo: string, commit: string, path: string): string {
  const p = path.split('/').map(encodeURIComponent).join('/');
  return `${ep.raw}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${commit}/${p}`;
}
